import { Component } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';

import { AdminService } from
  'src/app/services/admin/admin.service';


@Component({
  selector: 'app-x-short-course-details',
  templateUrl: './x-short-course-details.component.html',
  styleUrl: './x-short-course-details.component.scss'
})
export class XShortCourseDetailsComponent {

  // Store course type
  courseType: string = '';

  //Store showing Occurrence Picker
  showingOccurrencePicker = false;

  // Store course ID
  courseId: number = 0;

  // Store course details
  course: any = null;

  // Track loading state
  isLoading: boolean = false;

  // Track course not found state
  courseNotFound: boolean = false;

  // Track image load failure
  imageLoadFailed: boolean = false;

  // Store VOC details
  vocDetails: any = null;

  // Track VOC course type
  isVOC: boolean = false;

  //store previous url
  previousCourseUrl: string = '';

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private adminService: AdminService,
    private sanitizer: DomSanitizer
  ) {
  }

  // Initialise course details
  ngOnInit(): void {

    // Get previous URL from local storage
    this.previousCourseUrl =
      localStorage.getItem('PREVIOUS_COURSE_URL') || '';

    // Remove previous URL from local storage
    localStorage.removeItem('PREVIOUS_COURSE_URL');

    // Read route query parameters
    this.route.queryParams.subscribe(params => {

      // Store course type
      this.courseType =
        (params['type'] || 'UOC')
          .toLowerCase();

      // Store course ID from URL
      const id =
        Number(params['id']);

      // Validate course ID
      if (!id) {

        this.courseNotFound = true;
        return;
      }

      // Set course ID
      this.courseId = id;
      // Check VOC course
      if (this.courseType === 'voc') {
        this.isVOC = true;
      }


      // Load course details
      this.loadCourseDetails();

    });
  }


  // Load course details
  loadCourseDetails(): void {

    // Load VOC details
    if (this.isVOC) {

      this.loadVOCDetails(
        this.courseId
      );

      return;
    }


    // Load UOC course details
    this.loadUOCCourseDetails(
      this.courseId
    );
  }


  // Load VOC details
  loadVOCDetails(
    vocId: number
  ): void {

    // Start loading
    this.isLoading = true;

    // Reset VOC details
    this.vocDetails = null;

    this.adminService
      .getXVOCFullDetails(vocId)
      .subscribe({

        next: (response: any) => {

          // Get VOC response data
          const result =
            response?.data ??
            response?.Data ??
            response?.vocDetails ??
            response?.VocDetails ??
            response;

          // Store VOC details
          this.vocDetails =
            Array.isArray(result)
              ? result[0]
              : result;

          // Set course not found state
          this.courseNotFound =
            !this.vocDetails;

          // Stop loading
          this.isLoading = false;
        },

        error: (error: any) => {

          console.error(
            'Error loading VOC details:',
            error
          );

          // Mark course as not found
          this.courseNotFound = true;

          // Stop loading
          this.isLoading = false;
        }

      });
  }


  // Load UOC course details
  loadUOCCourseDetails(courseId: number): void {

    // Start loading
    this.isLoading = true;

    // Reset course not found state
    this.courseNotFound = false;

    // Reset image load state
    this.imageLoadFailed = false;

    // Reset course details
    this.course = null;


    this.adminService
      .GetXCourseFullDetails(
        courseId
      )
      .subscribe({

        next: (response: any) => {

          /*
           * Supports:
           * Object
           * OR [Object]
           */

          // Get course response data
          const data =
            Array.isArray(response)
              ? response[0]
              : response;


          // Validate course data
          if (!data) {

            this.courseNotFound = true;

            this.isLoading = false;

            return;
          }


          // Store course details
          this.course = data;


          // Reset course not found state
          this.courseNotFound = false;

          // Stop loading
          this.isLoading = false;


        },


        error: (error) => {

          console.error(
            'Course Details Error:',
            error
          );


          // Reset course details
          this.course = null;

          // Mark course as not found
          this.courseNotFound = true;

          // Stop loading
          this.isLoading = false;
        }

      });
  }


  // Get course code
  get courseCode(): string {

    return (
      this.course?.courseCode ??
      this.course?.CourseCode ??
      ''
    );
  }


  // Get course title
  get courseTitle(): string {

    return (
      this.course?.courseTitle ??
      this.course?.CourseTitle ??
      ''
    );
  }

  // Get course description
  get courseDescription(): string {

    const description =
      this.course?.description ??
      this.course?.Description ??
      '';

    /*
     * Rich-text content can contain &nbsp; / Unicode NBSP characters.
     * Non-breaking spaces prevent normal line wrapping and can make the
     * content card overflow horizontally. Convert only the spaces while
     * preserving the rest of the RTE HTML and formatting.
     */
    return this.normalizeRteSpacing(description);
  }

  // Get trusted HTML version
  get safeCourseDescription(): SafeHtml {
    return this.sanitizer.bypassSecurityTrustHtml(
      this.courseDescription
    );
  }


  // Get course short description
  get courseShortDescription(): string {

    return (
      this.course?.nShortDescription ??
      this.course?.NShortDescription ??
      ''
    );
  }


  // Get hero description
  get heroDescription(): string {

    // Store short description HTML
    const html =
      this.courseShortDescription;


    // Return default hero description
    if (!html) {

      return 'View course information, training requirements and upcoming course options.';
    }


    // Create temporary element
    const temp =
      document.createElement('div');

    // Set HTML content
    temp.innerHTML =
      html;


    // Get plain text
    const text =
      temp.textContent ??
      temp.innerText ??
      '';


    // Return full text when short enough
    if (text.length <= 210) {

      return text;
    }


    // Return shortened hero description
    return (
      text.substring(
        0,
        210
      ).trim() +
      '...'
    );
  }


  // Get course image URL
  get imageUrl(): string {

    return (
      this.course?.imageUrl ??
      this.course?.ImageUrl ??
      ''
    );
  }


  // Check course image availability
  get hasCourseImage(): boolean {

    return !!(
      this.imageUrl &&
      this.imageUrl.trim().length > 0 &&
      !this.imageLoadFailed
    );
  }


  // Handle image load error
  onImageError(event: Event): void {

    // Store image element
    const img = event.target as HTMLImageElement;

    // Set fallback image
    img.onerror = null;
    img.src = 'assets/images/default-long-course-hero.png';

    // Mark image load failure
    this.imageLoadFailed = true;
  }


  // Get brochure URL
  get brochureUrl(): string {

    return (
      this.course?.brochureUrl ??
      this.course?.BrochureUrl ??
      ''
    );
  }


  // Check brochure availability
  get hasBrochure(): boolean {

    return !!(
      this.brochureUrl &&
      this.brochureUrl.trim().length > 0
    );
  }


  // Check course price availability
  get hasPrice(): boolean {

    return (
      Number(
        this.course?.totalAmount ??
        0
      ) > 0
    );
  }


  // Get course price
  get coursePrice(): string {

    // Check price availability
    if (!this.hasPrice) {

      return 'Enquire for pricing';
    }


    // Store currency code
    const currency =
      this.course?.currencyCode ??
      'AUD';


    // Format course price
    return new Intl.NumberFormat(
      'en-AU',
      {
        style: 'currency',
        currency: currency
      }
    ).format(
      Number(
        this.course.totalAmount
      )
    );
  }


  // Check calendar availability
  get isCourseAvailableInCalendar(): boolean {

    return Boolean(
      this.course
        ?.isCourseAvailableInCalendar
    );
  }


  // Get course occurrences
  get courseOccurrences(): any[] {

    let occurrences: any[] = [];

    if (
      Array.isArray(
        this.course?.occurances
      )
    ) {

      occurrences =
        this.course.occurances;
    }

    else if (
      Array.isArray(
        this.course?.occurrences
      )
    ) {

      occurrences =
        this.course.occurrences;
    }


    const today =
      new Date();

    today.setHours(
      0,
      0,
      0,
      0
    );


    return occurrences
      .filter(
        occurrence => {

          if (
            !occurrence?.endDate
          ) {
            return false;
          }

          const endDate =
            new Date(
              occurrence.endDate
            );

          endDate.setHours(
            0,
            0,
            0,
            0
          );

          return endDate >= today;
        }
      )
      .sort(
        (a, b) =>
          new Date(a.startDate).getTime()
          -
          new Date(b.startDate).getTime()
      );
  }


  // Get unique course locations
  get courseLocations(): string[] {


    // Store course locations
    const locations =
      this.courseOccurrences
        .map(
          occurrence =>
            occurrence?.locationName ??
            occurrence?.location ??
            ''
        )
        .filter(
          location =>
            !!location &&
            location.trim().length > 0
        );


    // Return unique locations
    return [
      ...new Set(locations)
    ];
  }


  // Get course units
  get courseUnits(): any[] {

    return Array.isArray(
      this.course?.units
    )
      ? this.course.units
      : [];
  }


  // Get course for content
  get courseFor(): string {

    return (
      this.course?.nCourseFor ??
      this.course?.NCourseFor ??
      ''
    );
  }


  // Get covered content
  get covered(): string {

    return (
      this.course?.nCovered ??
      this.course?.NCovered ??
      ''
    );
  }


  // Get preconditions content
  get preconditions(): string {

    return (
      this.course?.nPreconditions ??
      this.course?.NPreconditions ??
      ''
    );
  }


  // Get course duration content
  get howLongCourse(): string {

    return (
      this.course?.nHowLongCourse ??
      this.course?.NHowLongCourse ??
      ''
    );
  }


  // Get knowledge assessment content
  get knowledgeAssessment(): string {

    return (
      this.course?.nKnowledgeAssessment ??
      this.course?.NKnowledgeAssessment ??
      ''
    );
  }


  // Get skills assessment content
  get skillsAssessment(): string {

    return (
      this.course?.nSkillsAssessment ??
      this.course?.NSkillsAssessment ??
      ''
    );
  }


  // Get certificate validity content
  get certificateValid(): string {

    return (
      this.course?.nCertificateValid ??
      this.course?.NCertificateValid ??
      ''
    );
  }


  // Check content availability
  hasContent(
    value: string | null | undefined
  ): boolean {

    // Check content value
    if (!value) {

      return false;
    }


    // Create temporary element
    const temp =
      document.createElement('div');


    // Set HTML content
    temp.innerHTML =
      value;


    // Check plain text content
    return !!(
      temp.textContent ??
      temp.innerText ??
      ''
    ).trim();
  }


  // Book selected course
  /*  bookCourse(): void {
     //if (this.course && this.courseOccurrences.length) {
       this.showingOccurrencePicker = true;
     //}
   } */

  // Book selected course
  bookCourse(): void {



    // Navigate to course calendar
    this.router.navigate(
      [
        '/course-calendar'
      ],
      {
        queryParams: {

          type:
            this.courseType,

          courseId:
            this.courseId,

          openCourse:
            true
        }
      }
    );
  }


  // Open course enquiry
  enquireNow(): void {

    // Navigate to single enquiry
    this.router.navigate(
      [
        '/single-enquire'
      ],
      {
        queryParams: {

          type:
            this.courseType.toLowerCase(),

          courseId:
            this.courseId,

          openCourse:
            true,

          isCourseAvailableInCalendar:
            this.isCourseAvailableInCalendar
        }
      }
    );
  }


  enquireMain() {

  /*   if (this.courseOccurrences.length > 0) {
      this.bookCourse();
    }
    else { */
      this.enquireNow();
    //}
  }

  // Return to course list
  // Return to course list
  goBack(): void {

    // Navigate to previous URL if available
    if (this.previousCourseUrl) {

      this.router.navigateByUrl(
        this.previousCourseUrl
      );

      return;
    }

    // Navigate to default course list
    this.router.navigate(
      ['/course-short'],
      {
        queryParams: {
          type: this.courseType.toLowerCase()
        }
      }
    );

  }

  // Get VOC code
  get vocCode(): string {

    return this.vocDetails?.vocCode ?? '';
  }


  // Get VOC name
  get vocName(): string {

    return this.vocDetails?.vocName ?? '';
  }


  // Get VOC description
  get vocDescription(): string {

    return this.vocDetails?.description ?? '';
  }


  // Get VOC delivery
  get vocDelivery(): string {

    return this.vocDetails?.delivery ?? '';
  }


  // Get VOC location
  get vocLocation(): string {

    return this.vocDetails?.location ?? '';
  }


  // Get VOC requirements
  get vocRequirements(): string {

    return this.vocDetails?.requisitesRequirements ?? '';
  }


  // Get VOC certification
  get vocCertification(): string {

    return this.vocDetails?.certification ?? '';
  }


  // Get VOC cost
  get vocCost(): number | null {

    return this.vocDetails?.cost ?? null;
  }


  // Convert non-breaking RTE spaces to normal spaces so text wraps naturally.
  private normalizeRteSpacing(
    value: string | null | undefined
  ): string {

    if (!value) {
      return '';
    }

    return value
      .replace(/&nbsp;/gi, ' ')
      .replace(/&#160;/gi, ' ')
      .replace(/&#xA0;/gi, ' ')
      .replace(/\u00A0/g, ' ');
  }


  // Remove HTML from content
  stripHtml(
    value: string | null | undefined
  ): string {

    // Check content value
    if (!value) {
      return '';
    }

    // Create temporary element
    const div =
      document.createElement('div');

    // Set HTML content
    div.innerHTML = value;

    // Return plain text
    return (
      div.textContent ||
      div.innerText ||
      ''
    ).trim();
  }

  openOccurrences(
    occurrence: any
  ): void {

    if (
      !this.course ||
      !occurrence?.id ||
      !occurrence?.startDate
    ) {
      return;
    }


    this.router.navigate(
      [
        '/course-calendar'
      ],
      {
        queryParams: {

          type:
            this.courseType.toLowerCase(),

          courseId:
            this.courseId,

          occurrenceId:
            occurrence.id,

          occurrenceDate:
            occurrence.startDate,

          openCourse:
            true
        }
      }
    );
  }




  // Add these getters to the public Course Details component class.
  // Add imageSizeMode/imageWidth/imageHeight/imageAlignment to the public Course DTO.

  get isHeroImageSized(): boolean {
    const mode = (this.course as any)?.imageSizeMode;
    return ['HEIGHT', 'WIDTH', 'CUSTOM'].includes(String(mode ?? '').toUpperCase());
  }

  // For the flex wrapper (only custom-sized images need justify-content).
  get heroImageJustifyContent(): 'flex-start' | 'center' | 'flex-end' {
    const alignment = String((this.course as any)?.imageAlignment ?? 'CENTER').toUpperCase();
    return alignment === 'LEFT' ? 'flex-start'
      : alignment === 'RIGHT' ? 'flex-end' : 'center';
  }

  get heroImageStyles(): Record<string, string> {
    const c: any = this.course;
    const mode = String(c?.imageSizeMode ?? '').toUpperCase();
    const width = Number(c?.imageWidth);
    const height = Number(c?.imageHeight);
    const alignment = String(c?.imageAlignment ?? 'CENTER').toUpperCase();
    const objectPosition = alignment === 'LEFT' ? 'left center'
      : alignment === 'RIGHT' ? 'right center' : 'center center';

    // Legacy cover images can use object-position too (no sizing changes).
    const styles: Record<string, string> = { 'object-position': objectPosition };

    if (mode === 'HEIGHT' && Number.isFinite(height) && height >= 40) {
      return { ...styles, width: 'auto', height: `${height}px`, 'object-fit': 'contain' };
    }
    if (mode === 'WIDTH' && Number.isFinite(width) && width >= 40) {
      return { ...styles, width: `${width}px`, height: 'auto', 'object-fit': 'contain' };
    }
    if (mode === 'CUSTOM' && Number.isFinite(width) && Number.isFinite(height)
      && width >= 40 && height >= 40) {
      return { ...styles, width: `${width}px`, height: `${height}px`, 'object-fit': 'contain' };
    }
    return styles;
  }

}